/* assets/wb-bon.js — Bon de commande (phrase éditable + cadence).
   - tokens texte : contenteditable, Entrée → token suivant
   - tokens nombre : glissement horizontal (pointer events, touch-action:none),
     flèches clavier (role=spinbutton), pavé − / + en mobile
   - calcul : cafés à servir, capacité, attente, verdict, silhouettes (05-DONNEES)
   - ticket : chaque ligne clignote quand sa valeur change
   - envoi : validation email puis soumission native du {% form 'contact' %} ;
     l'état « DEVIS DEMANDÉ ✓ » est rejoué au retour depuis sessionStorage
   - pré-remplissage depuis la query string du configurateur
     (?station=pq&baristas=2&duree=jour&invites=150&marque=…) */
(() => {
  'use strict';

  const SPEC = {
    baristas: { min: 1, max: 5, step: 1, per: 26 },
    guests: { min: 20, max: 900, step: 10, per: 3 },
    hours: { min: 1, max: 12, step: 0.5, per: 30 }
  };
  const DUREE_HOURS = { demi: 4, jour: 8, deux: 12, plus: 12 };
  const STORE_KEY = 'wb-bon-state';
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const reduce = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const fr = (n) => Number(n).toLocaleString('fr-FR');
  const hoursTxt = (h) => (h % 1 ? String(h).replace('.', ',') : String(h)) + ' h';

  function init() {
    const root = document.querySelector('[data-wb-bon]');
    if (!root || root.dataset.wbBound) return;
    root.dataset.wbBound = '1';

    const q = (s) => root.querySelector(s);
    const qa = (s) => Array.from(root.querySelectorAll(s));
    let copy = {};
    try { copy = JSON.parse(q('[data-wb-bon-copy]').textContent); } catch (_) { copy = {}; }
    const t = (k, d) => (copy[k] != null ? copy[k] : d || '');
    const list = (k, d) => String(t(k, d)).split(',').map((w) => w.trim());
    /* Mots traduits (sections.bon.*) : le rendu suit la langue de la page. */
    const NUM_WORDS = list('numWords', 'zéro,un,deux,trois,quatre,cinq');
    const unitBar = (n) => ' ' + (n > 1 ? t('uBaristas', 'baristas') : t('uBarista', 'barista'));

    const ratio = parseFloat(root.dataset.ratio) || 1.6;
    const rate = parseInt(root.dataset.rate, 10) || 70;

    const el = {
      caret: q('[data-wb-caret]'),
      tip: q('[data-wb-tip]'),
      hint: q('[data-wb-hint]'),
      col: q('[data-wb-col]'),
      aside: q('[data-wb-aside]'),
      scroll: q('[data-wb-scroll]'),
      tabs: qa('[data-wb-tab]'),
      tabBonLabel: q('[data-wb-tab-bon-label]'),
      pad: q('[data-wb-pad]'),
      padLabel: q('[data-wb-pad-label]'),
      mvTitle: q('[data-wb-mv-title]'),
      mvNote: q('[data-wb-mv-note]'),
      verdict: q('[data-wb-verdict]'),
      note: q('[data-wb-verdict-note]'),
      fixes: qa('[data-wb-fix]'),
      m1: q('[data-wb-m1]'), m2: q('[data-wb-m2]'), m3: q('[data-wb-m3]'),
      cups: q('[data-wb-cups]'),
      cupNote: q('[data-wb-cup-note]'),
      total: q('[data-wb-total]'),
      stamp: q('[data-wb-stamp]'),
      ref: q('[data-wb-ref]'),
      send: q('[data-wb-send]'),
      sendNote: q('[data-wb-send-note]'),
      assump: q('[data-wb-assump]'),
      form: q('#wb-bon-form'),
      posted: !!q('[data-wb-posted]')
    };
    const toks = qa('[data-tok]');
    const txt = {};
    toks.forEach((tk) => { if (tk.dataset.kind === 'text') txt[tk.dataset.tok] = tk; });
    const fields = { client: q('[data-wb-field="client"]'), societe: q('[data-wb-field="societe"]'), email: q('[data-wb-field="email"]'), tel: q('[data-wb-field="tel"]') };
    const rec = {};
    qa('[data-wb-r]').forEach((row) => { rec[row.dataset.wbR] = { row, val: row.querySelector('[data-wb-rv]') }; });
    const hidden = {};
    qa('[data-wb-f]').forEach((i) => { hidden[i.dataset.wbF] = i; });

    const cfg = {
      baristas: parseInt(root.dataset.baristas, 10) || 2,
      guests: parseInt(root.dataset.guests, 10) || 220,
      hours: parseFloat(root.dataset.hours) || 4
    };
    let station = '';
    let sent = false;
    /* touched : passe a true au premier geste reel de l utilisateur.
       On l expose via un accesseur pour que la demo s arrete d elle-meme,
       quel que soit l endroit du fichier qui declare le contact. */
    let touchedRaw = false;
    const state = {
      get touched() { return touchedRaw; },
      set touched(v) {
        if (v && !touchedRaw) {
          touchedRaw = true;
          if (typeof stopDemo === 'function') stopDemo();
        } else {
          touchedRaw = v;
        }
      },
    };
    let active = null;
    let drag = null;
    let mobile = false;
    let tab = 'write';
    let padKey = 'guests';
    let demoRunning = false;
    let demoT = 0, demoStep = 0, tipT = 0, noteT = 0, tipRaf = 0;

    /* ---------- pré-remplissage depuis le configurateur ---------- */
    const params = new URLSearchParams(location.search);
    if (params.get('station')) station = params.get('station');
    if (params.get('baristas')) cfg.baristas = clamp(parseInt(params.get('baristas'), 10) || cfg.baristas, 1, 5);
    if (params.get('invites')) cfg.guests = clamp(Math.round((parseInt(params.get('invites'), 10) || cfg.guests) / 10) * 10, 20, 900);
    if (params.get('duree') && DUREE_HOURS[params.get('duree')]) cfg.hours = DUREE_HOURS[params.get('duree')];
    if (params.get('marque') && txt.marque) txt.marque.textContent = params.get('marque');
    if (params.toString()) state.touched = true;

    /* ---------- calcul ---------- */
    const needed = () => Math.round(cfg.guests * ratio);
    const capacity = () => Math.round(cfg.baristas * rate * cfg.hours);
    const comptoir = () => {
      if (station === 'md') return t('stationMd', 'Medium');
      if (station === 'pq') return t('stationPq', 'Paquebot');
      return cfg.baristas >= 2 ? t('stationPq', 'Paquebot') : t('stationMd', 'Medium');
    };

    function paint() {
      const vBar = q('[data-wb-v="baristas"]'), uBar = q('[data-wb-u="baristas"]');
      if (vBar) vBar.textContent = cfg.baristas <= 5 ? NUM_WORDS[cfg.baristas] : String(cfg.baristas);
      if (uBar) uBar.textContent = unitBar(cfg.baristas);
      const vG = q('[data-wb-v="guests"]');
      if (vG) vG.textContent = String(cfg.guests);
      const vH = q('[data-wb-v="hours"]');
      if (vH) vH.textContent = hoursTxt(cfg.hours);
      toks.forEach((tk) => {
        if (tk.dataset.kind === 'num') tk.setAttribute('aria-valuenow', String(cfg[tk.dataset.tok]));
      });

      const need = needed(), cap = capacity();
      const cover = cap / need;
      const peakDemand = need * 0.5 / Math.max(0.5, cfg.hours / 3);
      const peakCap = cfg.baristas * rate;
      const wait = peakDemand <= peakCap ? (60 / peakCap) * (peakDemand / peakCap) * 4 : (peakDemand - peakCap) / peakCap * 22 + 6;

      if (el.m1) el.m1.textContent = fr(need);
      if (el.m2) el.m2.textContent = fr(cap);
      if (el.m3) el.m3.textContent = String(Math.max(1, Math.round(wait)));

      let title, note, warn = false;
      const maxCap = SPEC.baristas.max * rate * SPEC.hours.max;
      if (cover < 1) {
        warn = true;
        title = t('vManque');
        note = (maxCap < need ? t('nManqueMax') : t('nManque')).replace('{n}', fr(need - cap));
      } else if (wait > 12) {
        warn = true;
        title = t('vQueue');
        note = cfg.baristas >= SPEC.baristas.max ? t('nQueueMax') : t('nQueue');
      } else if (cover > 2.2) {
        title = t('vLarge');
        note = t('nLarge');
      } else {
        title = t('vOk');
        note = cfg.baristas > 1 ? t('nOk').replace('{b}', NUM_WORDS[cfg.baristas] || cfg.baristas) : t('nOkOne');
      }
      [el.verdict, el.mvTitle].forEach((n) => { if (n) { n.textContent = title; n.classList.toggle('is-warn', warn); } });
      [el.note, el.mvNote].forEach((n) => { if (n) n.textContent = note; });
      const showFix = cover < 1 && maxCap >= need;
      el.fixes.forEach((b) => { b.hidden = !showFix; });
      if (el.tabBonLabel) el.tabBonLabel.textContent = t('tabBon', 'LE BON') + ' · ' + fr(need) + ' ' + t('cafes', 'CAFÉS');

      paintRecap();
      paintPad();
      paintCups();
    }

    function paintRecap() {
      const tv = (k) => (txt[k] ? txt[k].textContent.trim() : '') || '—';
      const set = (k, v) => { if (rec[k] && rec[k].val) rec[k].val.textContent = v; };
      set('date', tv('date'));
      set('lieu', tv('lieu'));
      set('marque', tv('marque'));
      set('guests', fr(cfg.guests));
      set('baristas', cfg.baristas + unitBar(cfg.baristas));
      set('hours', hoursTxt(cfg.hours));
      set('comptoir', comptoir());
      if (el.total) el.total.textContent = fr(needed());
      Object.keys(fields).forEach((k) => {
        const v = (fields[k] && fields[k].value.trim()) || '';
        if (!rec[k] || !rec[k].val) return;
        rec[k].val.textContent = v || '—';
        rec[k].val.classList.toggle('is-empty', !v);
      });
      if (hidden.date) hidden.date.value = tv('date');
      if (hidden.lieu) hidden.lieu.value = tv('lieu');
      if (hidden.invites) hidden.invites.value = String(cfg.guests);
      if (hidden.baristas) hidden.baristas.value = String(cfg.baristas);
      if (hidden.heures) hidden.heures.value = String(cfg.hours);
      if (hidden.comptoir) hidden.comptoir.value = comptoir();
      if (hidden.marquage) hidden.marquage.value = tv('marque');
      if (hidden.cafes) hidden.cafes.value = String(needed());
      if (hidden.body) {
        hidden.body.value = 'Le ' + tv('date') + ', à ' + tv('lieu') + ', ' + cfg.baristas + (cfg.baristas > 1 ? ' baristas' : ' barista') + ' serviront ' + cfg.guests + ' invités pendant ' + hoursTxt(cfg.hours) + ', sur un comptoir ' + comptoir() + ' marqué ' + tv('marque') + '. Cafés à servir : ' + needed() + ' (capacité ' + capacity() + ').';
      }
    }

    function paintPad() {
      if (!el.padLabel) return;
      const name = { baristas: 'BARISTAS', guests: 'INVITÉS', hours: 'DURÉE' }[padKey];
      const v = padKey === 'hours' ? hoursTxt(cfg.hours).toUpperCase() : String(cfg[padKey]);
      el.padLabel.textContent = name + ' · ' + v;
    }

    function paintCups() {
      if (!el.cups) return;
      const need = needed(), cap = capacity();
      const icons = Math.ceil(cfg.guests / 5);
      const coverage = need ? Math.min(1, cap / need) : 1;
      const served = Math.round(icons * coverage);
      while (el.cups.childElementCount < icons) {
        const c = document.createElement('span');
        c.className = 'wb-cup';
        el.cups.appendChild(c);
      }
      Array.from(el.cups.children).forEach((c, i) => {
        c.hidden = i >= icons;
        c.classList.toggle('is-wait', i >= served);
      });
      const unserved = Math.max(0, cfg.guests - Math.round(cfg.guests * coverage));
      if (el.cupNote) el.cupNote.textContent = cap >= need ? t('cupsOk') : t('cupsWait').replace('{n}', fr(unserved));
    }

    /* ---------- interactions ---------- */
    function flash(key) {
      const r = rec[key];
      if (!r) return;
      clearTimeout(r.t);
      r.row.classList.add('is-flash');
      r.t = setTimeout(() => r.row.classList.remove('is-flash'), 340);
    }

    function setVal(key, v) {
      const sp = SPEC[key];
      v = clamp(Math.round(v / sp.step) * sp.step, sp.min, sp.max);
      if (v === cfg[key]) return;
      cfg[key] = v;
      if (sent) unsend();
      paint();
      flash(key);
      if (key === 'baristas') flash('comptoir');
    }
    const bump = (key, dir) => setVal(key, cfg[key] + dir * SPEC[key].step);

    function setActive(tk) {
      if (sent) return;
      if (tk.dataset.kind === 'num') { padKey = tk.dataset.tok; paintPad(); }
      if (active === tk) return;
      active = tk;
      toks.forEach((n) => n.classList.toggle('is-active', n === tk));
    }

    function nextTok(tk) {
      const i = toks.indexOf(tk);
      const n = toks[(i + 1) % toks.length];
      n.focus();
      setActive(n);
    }

    function showTip(tk) {
      if (!el.tip || !el.col || !tk || mobile) return;
      const r = tk.getBoundingClientRect(), h = el.col.getBoundingClientRect();
      el.tip.classList.add('is-shown');
      const left = Math.max(12, Math.min(r.left - h.left + r.width / 2 - el.tip.offsetWidth / 2, h.width - el.tip.offsetWidth - 12));
      el.tip.style.left = left + 'px';
      el.tip.style.top = (r.top - h.top + el.col.scrollTop - el.tip.offsetHeight - 10) + 'px';
      clearTimeout(tipT);
      cancelAnimationFrame(tipRaf);
      tipRaf = requestAnimationFrame(() => el.tip.classList.add('is-in'));
    }
    function hideTip() {
      if (!el.tip) return;
      cancelAnimationFrame(tipRaf);
      el.tip.classList.remove('is-in');
      clearTimeout(tipT);
      tipT = setTimeout(() => el.tip.classList.remove('is-shown'), 480);
    }

    toks.forEach((tk) => {
      const num = tk.dataset.kind === 'num';
      tk.addEventListener('pointerdown', (e) => {
        state.touched = true;
        hideTip();
        if (sent) return;
        setActive(tk);
        if (!num) return;
        e.preventDefault();
        tk.focus();
        drag = { key: tk.dataset.tok, x: e.clientX };
        try { tk.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
        document.body.style.cursor = 'ew-resize';
      });
      tk.addEventListener('focus', () => setActive(tk));
      tk.addEventListener('click', () => setActive(tk));
      tk.addEventListener('mouseenter', () => { if (!sent && num) showTip(tk); });
      tk.addEventListener('mouseleave', () => { if (num && !demoRunning) hideTip(); });
      if (!num) {
        tk.addEventListener('input', () => {
          const s = tk.textContent.replace(/\s+/g, ' ');
          if (s !== tk.textContent) tk.textContent = s;
          if (sent) unsend();
          paintRecap();
          flash(tk.dataset.tok);
        });
        tk.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); tk.blur(); nextTok(tk); }
        });
        tk.addEventListener('blur', () => {
          if (!tk.textContent.trim()) tk.textContent = tk.dataset.tok === 'marque' ? t('marqueDefault', 'votre marque') : '—';
          paintRecap();
        });
      } else {
        tk.addEventListener('keydown', (e) => {
          const d = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -1 : 0);
          if (!d) return;
          e.preventDefault();
          state.touched = true;
          bump(tk.dataset.tok, d);
        });
      }
    });

    window.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const sp = SPEC[drag.key];
      const steps = Math.round((e.clientX - drag.x) / sp.per);
      if (!steps) return;
      drag.x += steps * sp.per;
      setVal(drag.key, cfg[drag.key] + steps * sp.step);
    });
    const endDrag = () => { if (!drag) return; drag = null; document.body.style.cursor = ''; };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    const fixIt = () => {
      state.touched = true;
      const need = needed();
      const n = Math.min(SPEC.baristas.max, Math.ceil(need / (rate * cfg.hours)));
      setVal('baristas', n);
      if (capacity() < need) {
        const h = Math.ceil((need / (rate * cfg.baristas)) * 2) / 2;
        setVal('hours', h);
      }
    };
    el.fixes.forEach((b) => b.addEventListener('click', fixIt));
    qa('[data-wb-pad-step]').forEach((b) => b.addEventListener('click', () => { state.touched = true; bump(padKey, parseInt(b.dataset.wbPadStep, 10)); }));
    Object.keys(fields).forEach((k) => {
      const f = fields[k];
      if (!f) return;
      f.addEventListener('input', () => { if (sent) unsend(); f.classList.remove('is-error'); paintRecap(); flash(k); });
      f.addEventListener('focus', () => { state.touched = true; });
    });

    /* ---------- onglets mobile ---------- */
    const setTab = (name) => {
      tab = name;
      el.tabs.forEach((b) => b.classList.toggle('is-on', b.dataset.wbTab === name));
      showTab();
      window.scrollTo({ top: 0 });
    };
    const showTab = () => {
      if (!el.col || !el.aside) return;
      el.col.classList.toggle('is-hidden', mobile && tab !== 'write');
      el.aside.classList.toggle('is-hidden', mobile && tab !== 'bon');
    };
    el.tabs.forEach((b) => b.addEventListener('click', () => setTab(b.dataset.wbTab)));
    const goto = q('[data-wb-goto-bon]');
    if (goto) goto.addEventListener('click', () => setTab('bon'));

    function fit() {
      mobile = document.documentElement.clientWidth < 800;
      root.classList.toggle('is-side', !mobile);
      if (el.hint && !sent && !demoRunning) el.hint.textContent = mobile ? t('hintMobile') : t('hint');
      showTab();
    }
    window.addEventListener('resize', fit);

    /* ---------- envoi ---------- */
    function saveState() {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify({
          cfg, station,
          date: txt.date ? txt.date.textContent : '', lieu: txt.lieu ? txt.lieu.textContent : '', marque: txt.marque ? txt.marque.textContent : '',
          client: fields.client ? fields.client.value : '', societe: fields.societe ? fields.societe.value : '', email: fields.email ? fields.email.value : '',
          tel: fields.tel ? fields.tel.value : ''
        }));
      } catch (_) { /* noop */ }
    }
    function restoreState() {
      try {
        const s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
        if (!s) return false;
        Object.assign(cfg, s.cfg || {});
        station = s.station || '';
        if (txt.date && s.date) txt.date.textContent = s.date;
        if (txt.lieu && s.lieu) txt.lieu.textContent = s.lieu;
        if (txt.marque && s.marque) txt.marque.textContent = s.marque;
        if (fields.client) fields.client.value = s.client || '';
        if (fields.societe) fields.societe.value = s.societe || '';
        if (fields.email) fields.email.value = s.email || '';
        if (fields.tel) fields.tel.value = s.tel || '';
        sessionStorage.removeItem(STORE_KEY);
        return true;
      } catch (_) { return false; }
    }

    function markSent() {
      sent = true;
      state.touched = true;
      const d = new Date();
      const p = (n) => String(n).padStart(2, '0');
      if (el.ref) { el.ref.textContent = 'WB-' + p(d.getFullYear() % 100) + p(d.getMonth() + 1) + p(d.getDate()) + '-' + p(Math.floor(Math.random() * 90) + 10); el.ref.classList.add('is-sent'); }
      if (el.send) { el.send.textContent = t('sent'); el.send.classList.add('is-sent'); el.send.disabled = true; }
      if (el.caret) el.caret.classList.add('is-off');
      if (el.sendNote) { el.sendNote.textContent = t('sentNote'); el.sendNote.classList.remove('is-error'); }
      Object.keys(fields).forEach((k) => { if (fields[k]) fields[k].readOnly = true; });
      if (el.stamp) el.stamp.textContent = t('stampSent') + ' ' + d.toLocaleDateString('fr-FR') + ' · WILD BEANS PARIS';
      if (el.hint) el.hint.textContent = t('hintSent');
      toks.forEach((tk) => { tk.classList.add('is-sent'); tk.classList.remove('is-active'); if (tk.dataset.kind === 'text') tk.setAttribute('contenteditable', 'false'); });
    }

    function unsend() {
      sent = false;
      if (el.ref) { el.ref.textContent = 'BROUILLON'; el.ref.classList.remove('is-sent'); }
      if (el.send) { el.send.innerHTML = t('send') + ' <span aria-hidden="true">↗</span>'; el.send.classList.remove('is-sent'); el.send.disabled = false; }
      if (el.caret) el.caret.classList.remove('is-off');
      if (el.sendNote) el.sendNote.textContent = t('sendNote');
      if (el.hint) el.hint.textContent = mobile ? t('hintMobile') : t('hint');
      if (el.stamp) el.stamp.textContent = t('stamp');
      Object.keys(fields).forEach((k) => { if (fields[k]) fields[k].readOnly = false; });
      toks.forEach((tk) => { tk.classList.remove('is-sent'); if (tk.dataset.kind === 'text') tk.setAttribute('contenteditable', 'plaintext-only'); });
    }

    if (el.form) {
      /* Email et téléphone obligatoires : on bloque l'envoi et on pointe le champ. */
      const refuse = (e, field, msgKey) => {
        e.preventDefault();
        if (mobile) setTab('bon');
        if (field) { field.focus(); field.classList.add('is-error'); }
        if (el.sendNote) { el.sendNote.textContent = t(msgKey); el.sendNote.classList.add('is-error'); }
        clearTimeout(noteT);
        noteT = setTimeout(() => { if (el.sendNote) { el.sendNote.textContent = t('sendNote'); el.sendNote.classList.remove('is-error'); } }, 3200);
      };
      el.form.addEventListener('submit', (e) => {
        if (sent) { e.preventDefault(); return; }
        const mail = fields.email ? fields.email.value.trim() : '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail)) { refuse(e, fields.email, 'sendError'); return; }
        /* Chiffres, espaces, points, tirets, parenthèses, + en tête ; 8 à 15 chiffres. */
        const tel = fields.tel ? fields.tel.value.trim() : '';
        const digits = tel.replace(/\D/g, '').length;
        if (fields.tel && (!/^\+?[\d\s.()-]+$/.test(tel) || digits < 8 || digits > 15)) { refuse(e, fields.tel, 'sendErrorTel'); return; }
        paintRecap();
        saveState();
      });
    }

    /* ---------- découvrabilité : la phrase se joue toute seule ----------
       Une boucle continue montre les deux gestes possibles : on tape dans les
       mots, on glisse les nombres. Elle tourne jusqu au premier contact reel
       (clic, focus, touche, glissement) et ne reprend jamais ensuite.
       Rien n est memorise : chaque rechargement rejoue la demo. */
    const DEMO_TEXT = {
      date: list('demoDates', '14 juin,3 juillet,28 septembre'),
      lieu: ['Pantin', 'Le Marais', 'Saint-Ouen'],
      marque: list('demoMarques', 'votre marque,une maison de couture,un studio de tournage'),
    };

    let demoAlive = false;

    function stopDemo() {
      demoAlive = false;
      demoRunning = false;
      clearTimeout(demoT);
      clearTimeout(demoStep);
      toks.forEach((tk) => tk.classList.remove('is-active'));
      hideTip();
      restoreDemoDefaults();
      if (el.hint && !sent) el.hint.textContent = mobile ? t('hintMobile') : t('hint');
      paint();
    }

    /* Valeurs d origine, pour que la demo ne laisse jamais la phrase alteree. */
    const demoBase = {
      guests: 0, baristas: 0, hours: 0,
      date: '', lieu: '', marque: '',
    };
    /* Rien à restaurer tant que la démo n'a jamais tourné : sans ce garde-fou,
       un premier geste avant la démo remettait la phrase à « — » et 0. */
    let demoSnapped = false;
    function snapDemoDefaults() {
      demoSnapped = true;
      demoBase.guests = cfg.guests;
      demoBase.baristas = cfg.baristas;
      demoBase.hours = cfg.hours;
      Object.keys(DEMO_TEXT).forEach((k) => { if (txt[k]) demoBase[k] = txt[k].textContent; });
    }
    function restoreDemoDefaults() {
      if (!demoSnapped) return;
      cfg.guests = demoBase.guests;
      cfg.baristas = demoBase.baristas;
      cfg.hours = demoBase.hours;
      Object.keys(DEMO_TEXT).forEach((k) => { if (txt[k]) txt[k].textContent = demoBase[k]; });
    }

    const wait = (ms) => new Promise((res) => { demoStep = setTimeout(res, ms); });

    /* Efface puis retape un mot, lettre par lettre. */
    async function typeInto(key, word) {
      const tk = txt[key];
      if (!tk) return;
      tk.classList.add('is-active');
      showTip(tk);
      const from = tk.textContent;
      for (let i = from.length; i >= 0 && demoAlive; i--) {
        tk.textContent = from.slice(0, i);
        paint();
        await wait(34);
      }
      for (let i = 1; i <= word.length && demoAlive; i++) {
        tk.textContent = word.slice(0, i);
        paint();
        await wait(58);
      }
      if (!demoAlive) return;
      await wait(700);
      tk.classList.remove('is-active');
    }

    /* Fait glisser un nombre jusqu a une valeur, pas a pas. */
    async function slideTo(key, target) {
      const tk = q('[data-tok="' + key + '"]');
      if (!tk) return;
      tk.classList.add('is-active');
      showTip(tk);
      const step = SPEC[key].step;
      let v = cfg[key];
      while (v !== target && demoAlive) {
        v = target > v ? Math.min(target, v + step) : Math.max(target, v - step);
        setVal(key, v);
        showTip(tk);
        await wait(90);
      }
      if (!demoAlive) return;
      await wait(700);
      tk.classList.remove('is-active');
    }

    async function demoLoop() {
      if (state.touched || sent || reduce || mobile) return;
      demoAlive = true;
      demoRunning = true;
      snapDemoDefaults();
      if (el.hint) el.hint.textContent = t('hint');

      /* Une passe = un mot tape, un nombre glisse, en alternance. */
      const beats = [
        () => typeInto('lieu', DEMO_TEXT.lieu[1]),
        () => slideTo('guests', 320),
        () => typeInto('marque', DEMO_TEXT.marque[1]),
        () => slideTo('baristas', 3),
        () => typeInto('date', DEMO_TEXT.date[1]),
        () => slideTo('hours', 6),
        () => typeInto('lieu', DEMO_TEXT.lieu[2]),
        () => slideTo('guests', 180),
        () => typeInto('marque', DEMO_TEXT.marque[2]),
        () => slideTo('baristas', 2),
        () => typeInto('date', DEMO_TEXT.date[2]),
        () => slideTo('hours', 4),
        () => typeInto('lieu', DEMO_TEXT.lieu[0]),
        () => typeInto('marque', DEMO_TEXT.marque[0]),
        () => typeInto('date', DEMO_TEXT.date[0]),
        () => slideTo('guests', demoBase.guests),
      ];

      let i = 0;
      while (demoAlive && !state.touched && !sent) {
        await beats[i % beats.length]();
        if (!demoAlive) return;
        await wait(520);
        i++;
      }
      demoRunning = false;
    }

    /* ---------- boot ---------- */
    fit();
    if (el.posted) {
      restoreState();
      paint();
      markSent();
    } else {
      paint();
      /* La démo ne tourne que lorsque le configurateur est à l'écran : il est
         sous une intro de 100vh, inutile de repeindre la phrase hors champ. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => {
          if (en.isIntersecting) {
            if (!demoRunning) { clearTimeout(demoT); demoT = setTimeout(demoLoop, 1400); }
          } else {
            clearTimeout(demoT);
            if (demoRunning) stopDemo();
          }
        }, { threshold: 0.3 }).observe(root);
      } else {
        demoT = setTimeout(demoLoop, 1400);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
